import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';

export interface Portfolio {
  id: string;
  organization_id: string;
  owner_id: string;
  name: string;
  description?: string | null;
  status: 'active' | 'archived';
  color?: string | null;
  created_at: string;
  updated_at: string;
}

export interface PortfolioRollup {
  completion_percentage: number;
  total_projects: number;
  active_projects: number;
  completed_projects: number;
  projects_at_risk: number;
  total_tasks: number;
  completed_tasks: number;
  blocked_tasks: number;
  overdue_tasks: number;
  total_hours_logged: number;
}

export interface PortfolioProjectStats {
  id: string;
  organization_id: string;
  name: string;
  status: string;
  completion_percentage: number;
  total_tasks: number;
  completed_tasks: number;
  blocked_tasks: number;
  overdue_tasks: number;
  total_hours_logged: number;
  active_members: number;
  position?: number;
}

export interface PortfolioDetails {
  portfolio: Portfolio;
  projects: PortfolioProjectStats[];
  rollup: PortfolioRollup;
}

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

const API_BASE_URL = `${ENV.API_URL}/api/project-management/portfolios`;

async function parseJsonSafe(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export const portfoliosService = {
  async list(organizationId: string): Promise<{ data: Portfolio[]; error: any }> {
    try {
      const token = await getToken();
      const query = new URLSearchParams({ organization_id: organizationId });
      const res = await fetch(`${API_BASE_URL}?${query.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { data: [], error: json?.error || 'Failed to fetch portfolios' };
      return { data: (json?.data || []) as Portfolio[], error: null };
    } catch (e: any) {
      return { data: [], error: e?.message || 'Network error' };
    }
  },

  async getById(portfolioId: string, organizationId: string): Promise<{ data: PortfolioDetails | null; error: any }> {
    try {
      const token = await getToken();
      const query = new URLSearchParams({ organization_id: organizationId });
      const res = await fetch(`${API_BASE_URL}/${encodeURIComponent(portfolioId)}?${query.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { data: null, error: json?.error || 'Failed to fetch portfolio' };
      return { data: (json?.data || null) as PortfolioDetails | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async create(params: { organization_id: string; name: string; description?: string | null; status?: 'active' | 'archived'; color?: string | null; }): Promise<{ data: Portfolio | null; error: any }> {
    try {
      const token = await getToken();
      const res = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { data: null, error: json?.error || 'Failed to create portfolio' };
      return { data: (json?.data || null) as Portfolio | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async update(portfolioId: string, organizationId: string, updates: Partial<{ name: string; description: string | null; status: 'active' | 'archived'; color: string | null; }>): Promise<{ data: Portfolio | null; error: any }> {
    try {
      const token = await getToken();
      const query = new URLSearchParams({ organization_id: organizationId });
      const res = await fetch(`${API_BASE_URL}/${encodeURIComponent(portfolioId)}?${query.toString()}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...updates, organization_id: organizationId }),
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { data: null, error: json?.error || 'Failed to update portfolio' };
      return { data: (json?.data || null) as Portfolio | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async remove(portfolioId: string, organizationId: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const query = new URLSearchParams({ organization_id: organizationId });
      const res = await fetch(`${API_BASE_URL}/${encodeURIComponent(portfolioId)}?${query.toString()}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { error: json?.error || 'Failed to delete portfolio' };
      return { error: null };
    } catch (e: any) {
      return { error: e?.message || 'Network error' };
    }
  },

  async addProject(portfolioId: string, params: { organization_id: string; project_id: string; position?: number }): Promise<{ data: any; error: any }> {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/${encodeURIComponent(portfolioId)}/projects`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { data: null, error: json?.error || 'Failed to add project' };
      return { data: json?.data || null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async removeProject(portfolioId: string, params: { organization_id: string; project_id: string }): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const query = new URLSearchParams({ organization_id: params.organization_id, project_id: params.project_id });
      const res = await fetch(`${API_BASE_URL}/${encodeURIComponent(portfolioId)}/projects?${query.toString()}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { error: json?.error || 'Failed to remove project' };
      return { error: null };
    } catch (e: any) {
      return { error: e?.message || 'Network error' };
    }
  },
};
